import { useRef } from 'react';
import { Paperclip, Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ATTACHMENT_ACCEPT } from './TicketAttachments';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

export function AttachmentPicker({ files, onChange, disabled }: Props) {
  const { t } = useTranslation(['tickets', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);

  function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (picked.length === 0) return;
    const seen = new Set(files.map((f) => `${f.name}|${f.size}`));
    const deduped = picked.filter((f) => !seen.has(`${f.name}|${f.size}`));
    onChange([...files, ...deduped]);
  }

  function removeAt(idx: number) {
    onChange(files.filter((_, i) => i !== idx));
  }

  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-slate-400" />
          <h4 className="text-sm font-semibold text-slate-700">
            {t('tickets:attachmentPicker.title')}
            {files.length > 0 ? ` (${files.length})` : ''}
          </h4>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          <Upload className="w-3 h-3" />
          {t('tickets:attachmentPicker.add')}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={handlePick}
          className="hidden"
        />
      </div>

      {files.length === 0 ? (
        <p className="text-xs text-slate-400">{t('tickets:attachmentPicker.hint')}</p>
      ) : (
        <ul className="space-y-1.5">
          {files.map((file, idx) => (
            <li
              key={`${file.name}-${file.size}-${idx}`}
              className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5"
            >
              <span className="flex-1 truncate" title={file.name}>{file.name}</span>
              <span className="text-xs text-slate-400 shrink-0">{formatFileSize(file.size)}</span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                disabled={disabled}
                className="p-0.5 text-slate-400 hover:text-red-500 transition-colors rounded"
                aria-label={t('tickets:attachmentPicker.remove')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
